<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import { useCaptain } from 'dashboard/composables/useCaptain';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const { fetchLimits, responseLimits } = useCaptain();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');
const copilotThreads = useMapGetter('copilotThreads/getRecords');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const loadingThread = ref(false);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;

  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    if (preferredAssistant) return preferredAssistant;
  }

  if (inboxAssistant.value?.id) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  return assistants.value[0];
});

const conversationSelected = computed(() => Boolean(currentChat.value?.id));
const inboxUnbound = computed(
  () => conversationSelected.value && !inboxAssistant.value?.id
);
const quotaExhausted = computed(
  () => responseLimits.value?.currentAvailable === 0
);
const canSend = computed(
  () =>
    Boolean(activeAssistant.value?.id) &&
    !inboxUnbound.value &&
    !quotaExhausted.value
);

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  return isCaptainEnabled && isCopilotPanelOpen && !uiFlags.value.fetchingList;
});

const handleReset = () => {
  selectedCopilotThreadId.value = null;
};

const selectThread = async thread => {
  selectedCopilotThreadId.value = thread.id;
  loadingThread.value = true;
  try {
    await store.dispatch('copilotMessages/get', thread.id);
  } catch (error) {
    useAlert(error.message);
  } finally {
    loadingThread.value = false;
  }
};

const sendMessage = async message => {
  if (!canSend.value) return;

  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

watch(
  () => currentChat.value?.id,
  conversationId => {
    store.dispatch('getInboxCaptainAssistantById', conversationId);
  },
  { immediate: true }
);

watch(
  shouldShowCopilotPanel,
  open => {
    if (!open) return;
    fetchLimits();
    store.dispatch('copilotThreads/get');
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('captainAssistants/get');
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      :inbox-unbound="inboxUnbound"
      :quota-exhausted="quotaExhausted"
      :threads="copilotThreads"
      :selected-thread-id="selectedCopilotThreadId"
      :loading-thread="loadingThread"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
      @select-thread="selectThread"
    />
  </div>
  <template v-else />
</template>
